import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon.tsx';
import { comparisonApi, type Comparison, type ComparisonResponse } from '../features/comparison/api.ts';
import { downloadPdf, documentsApi } from '../features/documents/api.ts';
import { useConfirm } from './ConfirmDialog.tsx';
import { GoodsReceivedDialog } from './GoodsReceivedDialog.tsx';
import { useAuthStore } from '../store/auth.ts';
import { canProcure } from '../lib/roles.ts';
import { formatDateTime } from '../lib/format.ts';
import { t } from '../lib/strings.ts';

interface Props {
  requestId: string;
}

function fmtMvr(n: number | null | undefined): string {
  if (n == null) return '—';
  return `MVR ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}


// Tint applied to a vendor's column/card: strongest for the awarded winner,
// a lighter wash for the current cheapest (before award).
function toneClass(isWinner: boolean, isBest: boolean, isClosed: boolean): string {
  if (isWinner) return 'bg-[hsl(var(--success)/0.10)]';
  if (isBest && !isClosed) return 'bg-[hsl(var(--success)/0.05)]';
  return '';
}

function RankChip({ rank, highlight }: { rank: number | null; highlight: boolean }): JSX.Element {
  return (
    <span
      className={[
        'mono-meta num flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs',
        highlight ? 'border-[hsl(var(--success))] text-[hsl(var(--success))]' : 'border-border text-muted-foreground',
      ].join(' ')}
      aria-label={`${t.requestDetail.rank} ${rank ?? '—'}`}
    >
      {rank ?? '—'}
    </span>
  );
}

function VendorPills({ isWinner, isBest, isClosed, complete }: { isWinner: boolean; isBest: boolean; isClosed: boolean; complete: boolean }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1">
      {isWinner && <span className="pill pill-success">{t.requestDetail.winner}</span>}
      {isBest && !isClosed && <span className="pill pill-success">{t.requestDetail.bestPrice}</span>}
      {!complete && <span className="pill pill-danger">{t.requestDetail.incomplete}</span>}
    </div>
  );
}

// Quotation (and, for the winner, invoice) PDF downloads. The invoice only
// exists once the vendor has confirmed it — before that there is no
// legitimate invoice to download, so the button is hidden entirely rather
// than shown disabled or triggering an auto-generated draft.
function DownloadButtons({ responseId, isWinner, invoiceConfirmed }: { responseId: string; isWinner: boolean; invoiceConfirmed: boolean }): JSX.Element {
  const [dlQuote, setDlQuote] = useState(false);
  const [dlInv, setDlInv] = useState(false);

  async function handleQuotation() {
    setDlQuote(true);
    try { await downloadPdf(documentsApi.quotationUrl(responseId), `quotation-${responseId}.pdf`); }
    finally { setDlQuote(false); }
  }
  async function handleInvoice() {
    setDlInv(true);
    try { await downloadPdf(documentsApi.invoiceUrl(responseId), `invoice-${responseId}.pdf`); }
    finally { setDlInv(false); }
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      <button onClick={() => void handleQuotation()} disabled={dlQuote} className="btn-secondary btn-sm gap-1" title={t.requestDetail.dlQuotationTitle}>
        <Icon name="description" size={14} />
        {dlQuote ? t.requestDetail.opening : t.requestDetail.quotation}
      </button>
      {isWinner && invoiceConfirmed && (
        <button onClick={() => void handleInvoice()} disabled={dlInv} className="btn-primary btn-sm gap-1" title={t.requestDetail.dlInvoiceTitle}>
          <Icon name="receipt_long" size={14} />
          {dlInv ? t.requestDetail.opening : t.requestDetail.invoice}
        </button>
      )}
    </div>
  );
}

// Finance handoff for the winner: officer marks goods received (attaching
// PO(s) + other docs), which SMSes the vendor an invoice-confirm link. The
// award then waits for the vendor to confirm before it auto-forwards to
// Finance. Three states: sent (existing paid/sent badge), awaiting vendor
// confirmation (pill + resend link button), or not yet started (goods-received
// button). The preview button stays visible whenever the officer can act.
function AwardFinanceStatus({
  requestId,
  paid,
  sentToFinanceAt,
  goodsReceivedAt,
  invoiceConfirmedAt,
  canSend,
}: {
  requestId: string;
  paid: boolean;
  sentToFinanceAt: string | null;
  goodsReceivedAt: string | null;
  invoiceConfirmedAt: string | null;
  canSend: boolean;
}) {
  // invoiceConfirmedAt is not yet surfaced as a distinct visual state: once the
  // vendor confirms, the backend auto-forwards to Finance, which flips
  // sentToFinanceAt and this component shows the existing "sent" badge.
  const isSent = sentToFinanceAt != null;
  const awaitingConfirm = !isSent && goodsReceivedAt != null;
  const [previewing, setPreviewing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendErr, setResendErr] = useState<string | null>(null);

  const resendMutation = useMutation({
    mutationFn: () => comparisonApi.resendInvoiceLink(requestId),
    onSuccess: () => setResendSent(true),
    onError: () => setResendErr(t.requestDetail.resendInvoiceLinkFailed),
  });

  async function handlePreview() {
    setPreviewing(true);
    try { await downloadPdf(comparisonApi.paymentPackageUrl(requestId), `liyekunthah-${requestId}.pdf`); }
    finally { setPreviewing(false); }
  }

  const badge = paid ? (
    <span className="pill pill-success">{t.requestDetail.paidBadge}</span>
  ) : isSent ? (
    <span className="pill pill-pending">{t.requestDetail.sentToFinanceBadge}</span>
  ) : awaitingConfirm ? (
    <span className="pill pill-pending">{t.requestDetail.awaitingVendorConfirm}</span>
  ) : null;

  const showGoodsReceived = canSend && !isSent && !awaitingConfirm;
  if (!badge && !showGoodsReceived && !canSend) return null;

  return (
    <div className="flex flex-col items-start gap-2">
      {badge}
      <div className="flex flex-wrap items-center gap-2">
        {showGoodsReceived && (
          <button type="button" onClick={() => setDialogOpen(true)} className="btn-primary btn-sm">
            {t.requestDetail.goodsReceivedConfirm}
          </button>
        )}
        {canSend && awaitingConfirm && (
          <button
            type="button"
            onClick={() => { setResendSent(false); setResendErr(null); resendMutation.mutate(); }}
            disabled={resendMutation.isPending}
            className="btn-secondary btn-sm"
          >
            {t.requestDetail.resendInvoiceLink}
          </button>
        )}
        {resendSent && <span className="text-xs text-muted-foreground">{t.requestDetail.resendInvoiceLinkSent}</span>}
        {resendErr && <span className="text-xs text-destructive">{resendErr}</span>}
        {canSend && (
          <button type="button" onClick={() => void handlePreview()} disabled={previewing} className="btn-secondary btn-sm gap-1">
            <Icon name="visibility" size={14} />
            {previewing ? t.requestDetail.previewLoading : t.requestDetail.previewLiyekunthah}
          </button>
        )}
      </div>
      {showGoodsReceived && <GoodsReceivedDialog requestId={requestId} open={dialogOpen} onOpenChange={setDialogOpen} />}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string | number; accent?: boolean }): JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="status-label text-muted-foreground">{label}</span>
      <span className={['num tabular text-base font-semibold', accent ? 'text-[hsl(var(--success))]' : 'text-foreground'].join(' ')}>{value}</span>
    </div>
  );
}

export function ComparisonTable({ requestId }: Props): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const canAward = canProcure(role);
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const { data: comparison, isLoading, isError } = useQuery<Comparison>({
    queryKey: ['comparison', requestId],
    queryFn: () => comparisonApi.get(requestId),
  });

  const awardMutation = useMutation({
    mutationFn: (responseId: string) => comparisonApi.award(requestId, responseId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comparison', requestId] });
      void qc.invalidateQueries({ queryKey: ['request', requestId] });
    },
  });

  async function handleAward(responseId: string, vendorName: string): Promise<void> {
    const ok = await confirm({
      title: t.requestDetail.awardBtn,
      body: `${t.requestDetail.awardTo} ${vendorName}؟`,
      confirmLabel: t.requestDetail.awardConfirmBtn,
      danger: false,
    });
    if (ok) awardMutation.mutate(responseId);
  }

  const heading = (
    <div className="section-heading">
      <h2 className="status-label text-muted-foreground">{t.requestDetail.quoteComparison}</h2>
    </div>
  );

  if (isLoading) {
    return <div className="space-y-2">{heading}<p className="text-sm text-muted-foreground">{t.requestDetail.loadingComparison}</p></div>;
  }
  if (isError || !comparison) {
    return <div className="space-y-2">{heading}<p className="text-sm text-destructive">{t.requestDetail.comparisonError}</p></div>;
  }

  const { responses, lowestCompleteTotal, winnerResponseId, awardPaid, awardSentToFinanceAt, goodsReceivedAt, invoiceConfirmedAt } = comparison;
  const isClosed = winnerResponseId != null;

  if (responses.length === 0) {
    return <div className="space-y-2">{heading}<p className="text-sm text-muted-foreground">{t.requestDetail.noResponses}</p></div>;
  }

  // Complete quotes ranked cheapest-first; incomplete quotes trail, unranked.
  const ranked = responses.filter((r) => r.complete).sort((a, b) => a.total - b.total);
  const unranked = responses.filter((r) => !r.complete);
  const columns: ComparisonResponse[] = [...ranked, ...unranked];
  const bestId = ranked[0]?.responseId ?? null;
  const rankOf = (id: string): number | null => {
    const i = ranked.findIndex((r) => r.responseId === id);
    return i >= 0 ? i + 1 : null;
  };

  const lowestTotal = lowestCompleteTotal ?? ranked[0]?.total ?? null;
  const highestTotal = ranked.length ? Math.max(...ranked.map((r) => r.total)) : null;
  const savings = ranked.length >= 2 && highestTotal != null && lowestTotal != null ? highestTotal - lowestTotal : 0;

  const winner = columns.find((c) => c.responseId === winnerResponseId) ?? null;

  const awardCell = (col: ComparisonResponse) => {
    if (!isClosed && canAward) {
      return (
        <button
          type="button"
          onClick={() => void handleAward(col.responseId, col.vendorName)}
          disabled={!col.complete || awardMutation.isPending}
          className="btn-primary btn-sm w-full justify-center"
          title={!col.complete ? t.requestDetail.awardDisabledTitle : t.requestDetail.awardEnabledTitle}
        >
          {t.requestDetail.awardBtn}
        </button>
      );
    }
    if (isClosed) {
      return (
        <div className="flex flex-col items-center gap-1.5">
          {col.responseId === winnerResponseId && <span className="pill pill-success">{t.requestDetail.winner}</span>}
          <DownloadButtons responseId={col.responseId} isWinner={false} invoiceConfirmed={false} />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="section-heading">
        <h2 className="status-label text-muted-foreground">
          {t.requestDetail.quoteComparison} (<span className="num">{responses.length}</span> {responses.length !== 1 ? t.requestDetail.responses : t.requestDetail.response})
        </h2>
      </div>

      {/* Decision strip — the answer before the detail. */}
      {ranked.length > 0 && (
        <div className="card-paper flex flex-wrap items-center gap-x-8 gap-y-3 px-4 py-3">
          <Stat label={t.requestDetail.bidsLabel} value={responses.length} />
          <Stat label={t.requestDetail.lowestTotalLabel} value={fmtMvr(lowestTotal)} accent />
          {savings > 0 && <Stat label={t.requestDetail.savingsLabel} value={fmtMvr(savings)} />}
        </div>
      )}

      {/* Quotations — cheapest first: vendor · total · sent-at · download · contract. */}
      <ul className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
        {columns.map((col) => {
          const isWinner = col.responseId === winnerResponseId;
          const isBest = col.responseId === bestId;
          return (
            <li
              key={col.responseId}
              className={['flex flex-wrap items-center justify-between gap-x-4 gap-y-3 p-4', toneClass(isWinner, isBest, isClosed)].join(' ')}
            >
              <div className="flex min-w-0 items-center gap-3">
                <RankChip rank={rankOf(col.responseId)} highlight={(isBest && !isClosed) || isWinner} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-semibold leading-snug">{col.vendorName}</span>
                    <VendorPills isWinner={isWinner} isBest={isBest} isClosed={isClosed} complete={col.complete} />
                  </div>
                  <div className="mono-meta text-muted-foreground mt-0.5">
                    {t.requestDetail.submittedAtLabel} <span className="num" dir="ltr">{formatDateTime(col.submittedAt)}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="text-end">
                  <span className="num tabular text-lg font-semibold">{fmtMvr(col.total)}</span>
                  {!col.complete && <span className="ms-1 text-xs font-normal text-muted-foreground">{t.requestDetail.partial}</span>}
                </div>
                <DownloadButtons responseId={col.responseId} isWinner={false} invoiceConfirmed={false} />
                {!isClosed && canAward && (
                  <button
                    type="button"
                    onClick={() => void handleAward(col.responseId, col.vendorName)}
                    disabled={!col.complete || awardMutation.isPending}
                    className="btn-primary btn-sm"
                    title={!col.complete ? t.requestDetail.awardDisabledTitle : t.requestDetail.awardEnabledTitle}
                  >
                    {t.requestDetail.awardBtn}
                  </button>
                )}
                {isClosed && isWinner && <span className="pill pill-success">{t.requestDetail.winner}</span>}
              </div>
            </li>
          );
        })}
      </ul>

      {awardMutation.isError && <p className="text-sm text-destructive">{t.requestDetail.awardFailed}</p>}

      {/* Decision area — the awarded vendor + finance handoff, shown once. */}
      {isClosed && winner && (
        <section className="card-paper overflow-hidden border-s-4 border-[hsl(var(--success))]">
          {/* Award banner — vendor on one side, amount on the other, full width. */}
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 bg-[hsl(var(--success)/0.06)] px-4 py-3.5">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-[hsl(var(--success))] text-[hsl(var(--success))]" aria-hidden="true">
                <Icon name="check" size={20} />
              </span>
              <div className="leading-tight">
                <div className="status-label text-muted-foreground">{t.requestDetail.awardedTo}</div>
                <div className="text-lg font-semibold">{winner.vendorName}</div>
              </div>
            </div>
            <div className="text-end leading-tight">
              <div className="status-label text-muted-foreground">{t.requestDetail.totalRow}</div>
              <div className="num tabular text-xl font-semibold text-[hsl(var(--success))]">{fmtMvr(winner.total)}</div>
            </div>
          </div>

          {/* Action row — finance handoff on one side, documents on the other. */}
          <div className="flex flex-col gap-4 border-t border-border p-4 md:flex-row md:items-start md:justify-between">
            <AwardFinanceStatus
              requestId={requestId}
              paid={awardPaid}
              sentToFinanceAt={awardSentToFinanceAt}
              goodsReceivedAt={goodsReceivedAt}
              invoiceConfirmedAt={invoiceConfirmedAt}
              canSend={canAward}
            />
            <div className="md:text-end">
              <div className="status-label mb-1.5 text-muted-foreground">{t.requestDetail.documentsLabel}</div>
              <DownloadButtons responseId={winner.responseId} isWinner invoiceConfirmed={invoiceConfirmedAt != null} />
            </div>
          </div>
        </section>
      )}

      {dialog}
    </div>
  );
}
